// /home/szymon/kreator2/frontend/src/components/editor/hooks/useEditorLocalCallbacks.js
// v1.1.2 – Zmieniono logikę skalowania SVG dla lepszej jakości

import { useCallback, useEffect, useMemo } from "react";
// Importujemy funkcję do obliczania centrowania
import { calculateCenteredX } from '../snapUtils';

export function useEditorLocalCallbacks({
  pages,
  setPages,
  currentPage,
  selectedShapeId,
  setSelectedShapeId,
  pushToHistory, // Odbieramy funkcję do zapisywania w lokalnej historii
  changesCountRef,
  publicMode,
  isAccepted,
  doAutoSave,
  handleManualSave,
  backendUrl
}) {
  // Pomocnicza do modyfikowania obecnej strony
  const updateCurrentPage = useCallback(
    (fn) => {
      setPages((prev) => {
        const arr = [...prev];
        if (currentPage < 0 || currentPage >= arr.length) return prev;
        const copyPage = { ...arr[currentPage] };
        arr[currentPage] = fn(copyPage);
        return arr;
      });
    },
    [setPages, currentPage]
  );

  // Znajdujemy aktualnie zaznaczony obiekt
  const foundObj = useMemo(() => {
    if (!pages || pages.length === 0 || currentPage < 0 || currentPage >= pages.length || !selectedShapeId) return null;
    const pg = pages[currentPage];
    let obj = pg.texts.find((t) => t.id === selectedShapeId);
    if (obj) return { ...obj, _type: "text" };
    obj = pg.images.find((im) => im.id === selectedShapeId);
    if (obj) return { ...obj, _type: "image" };
    obj = pg.svgs.find((sv) => sv.id === selectedShapeId);
    if (obj) return { ...obj, _type: "svg" };
    return null;
  }, [pages, currentPage, selectedShapeId]);

  // Odznaczanie zablokowanych
  useEffect(() => {
    if (foundObj && publicMode && foundObj.flags?.locked) {
      setSelectedShapeId(null);
    }
  }, [foundObj, publicMode, setSelectedShapeId]);

  // Auto-zapis i licznik zmian (bez zmian)
  const doAutoSaveLocal = useCallback(() => { handleManualSave(pages); }, [handleManualSave, pages]);
  const incChangesAndMaybeAutoSave = useCallback(() => { changesCountRef.current++; if (changesCountRef.current >= 5) { doAutoSaveLocal(); changesCountRef.current = 0; } }, [changesCountRef, doAutoSaveLocal]);

  // --- Callbacki modyfikujące stan (ZMIENIONO handleChangeTextLocal) ---
  const handleChangeTextLocal = useCallback((updateData) => {
    if (isAccepted && publicMode) return;
    if (!updateData?.id) return; // Potrzebujemy ID

    // Znajdź oryginalny obiekt PRZED aktualizacją stanu
    const originalObj = pages[currentPage]?.texts.find(t => t.id === updateData.id);
    if (!originalObj) {
      console.warn(`[handleChangeTextLocal] Nie znaleziono obiektu o ID: ${updateData.id}`);
      return;
    }

    // Zapisz w historii, jeśli to zaznaczony obiekt
    if (foundObj && foundObj.id === updateData.id && foundObj._type === "text") {
      pushToHistory({ ...originalObj }); // Zapisujemy stan *przed* zmianą
    }

    let finalUpdateData = { ...updateData };

    // --- Dodana logika centrowania ---
    const isCentered = originalObj.alignment === 'center';
    const isPersonalized = originalObj.flags?.personalize || originalObj.flags?.personalization;

    // Sprawdzamy, czy dostaliśmy nową szerokość i czy obiekt wymaga centrowania
    if (finalUpdateData.width !== undefined && isCentered && isPersonalized) {
      const currentPageData = pages[currentPage];
      if (currentPageData) {
        // Potrzebujemy zaimportować calculateCenteredX z ../snapUtils
        // Zakładamy, że import został dodany na górze pliku
        const newX = calculateCenteredX(
          { ...originalObj, width: finalUpdateData.width }, // Użyj nowej szerokości do obliczeń
          currentPageData.guides || [],
          currentPageData.baseWidth || 800,
          currentPageData.baseHeight || 600
        );

        if (newX !== null && Math.abs(newX - (originalObj.x || 0)) > 0.1) {
          console.log(`[handleChangeTextLocal] Centering applied for ${originalObj.id}. New X: ${newX}`);
          finalUpdateData.x = newX; // Dodaj obliczone X do danych aktualizacji
        }
      }
    }
    // --- Koniec logiki centrowania ---

    updateCurrentPage((pg) => {
      pg.texts = pg.texts.map((t) => {
        if (t.id === finalUpdateData.id) {
          const mergedObject = { ...t, ...finalUpdateData };
          // --- ADD LOGGING ---
          console.log(`[handleChangeTextLocal] Updating ID: ${t.id}`);
          console.log(`  Original t:`, JSON.stringify(t));
          console.log(`  finalUpdateData:`, JSON.stringify(finalUpdateData));
          console.log(`  Merged object:`, JSON.stringify(mergedObject));
          // --- END LOGGING ---
          return mergedObject;
        }
        return t;
      });
      return pg;
    });
    incChangesAndMaybeAutoSave();
  }, [isAccepted, publicMode, pages, currentPage, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]); // Dodano 'pages' i 'currentPage' do zależności

  const handleChangeImageLocal = useCallback((chg) => { if (isAccepted && publicMode) return; if (!chg?.id) return; if (foundObj && foundObj.id === chg.id && foundObj._type === "image") { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { pg.images = pg.images.map((im) => im.id === chg.id ? { ...im, ...chg } : im); return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);
  const handleChangeSvgLocal = useCallback((chg) => { if (isAccepted && publicMode) return; if (!chg?.id) return; if (foundObj && foundObj.id === chg.id && foundObj._type === "svg") { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { pg.svgs = pg.svgs.map((sv) => (sv.id === chg.id ? { ...sv, ...chg } : sv)); return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);
  const handleColorChangeLocal = useCallback((obj, colorArr) => { if (!obj || !colorArr) return; if (isAccepted && publicMode) return; if (foundObj && foundObj.id === obj.id) { pushToHistory({ ...foundObj }); } if (obj._type === "text") { handleChangeTextLocal({ id: obj.id, color: colorArr }); } else if (obj._type === "svg") { handleChangeSvgLocal({ id: obj.id, color: colorArr }); } }, [isAccepted, publicMode, foundObj, pushToHistory, handleChangeTextLocal, handleChangeSvgLocal]);
  
  const handleLayerUpLocal = useCallback((shapeId) => { 
    console.log('[LAYER UP] Called with shapeId:', shapeId);
    console.log('[LAYER UP] isAccepted:', isAccepted, 'publicMode:', publicMode);
    
    if (isAccepted && publicMode) {
      console.log('[LAYER UP] Blocked: project accepted in public mode');
      return; 
    }
    if (!shapeId) {
      console.log('[LAYER UP] Blocked: no shapeId provided');
      return; 
    }
    if (foundObj && foundObj.id === shapeId) { 
      console.log('[LAYER UP] Pushing to history:', foundObj);
      pushToHistory({ ...foundObj }); 
    } 
    updateCurrentPage((pg) => {
      console.log('[LAYER UP] Current page data:', pg);
      
      // Zbieramy wszystkie obiekty z wszystkich tablic
      const allObjects = [
        ...pg.texts.map(obj => ({ ...obj, _type: 'text', _arrayName: 'texts' })),
        ...pg.images.map(obj => ({ ...obj, _type: 'image', _arrayName: 'images' })),
        ...pg.svgs.map(obj => ({ ...obj, _type: 'svg', _arrayName: 'svgs' }))
      ];
      console.log('[LAYER UP] All objects collected (before sort):', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Znajdź obiekt do przesunięcia
      const targetObj = allObjects.find(obj => obj.id === shapeId);
      console.log('[LAYER UP] Target object:', targetObj ? {id: targetObj.id, type: targetObj._type, subZ: targetObj.subZ, arrayName: targetObj._arrayName} : null);
      if (!targetObj) {
        console.log('[LAYER UP] Target object not found, returning unchanged page');
        return pg;
      }

      // Sortuj wszystkie obiekty według subZ
      allObjects.sort((a, b) => (a.subZ || 0) - (b.subZ || 0));
      console.log('[LAYER UP] Objects sorted by subZ:', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Znajdź indeks obiektu w posortowanej tablicy
      const currentIndex = allObjects.findIndex(obj => obj.id === shapeId);
      console.log('[LAYER UP] Current index:', currentIndex, 'Total objects:', allObjects.length);

      // Jeśli obiekt jest już na górze, nie rób nic
      if (currentIndex >= allObjects.length - 1) {
        console.log('[LAYER UP] Object already at the top');
        return pg;
      }

      // Przenieś obiekt o jedną pozycję wyżej
      const reorderedObjects = [...allObjects];
      const [movedObject] = reorderedObjects.splice(currentIndex, 1);
      reorderedObjects.splice(currentIndex + 1, 0, movedObject);
      console.log('[LAYER UP] Objects reordered (before new subZ):', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Przypisz nowe wartości subZ
      reorderedObjects.forEach((obj, index) => {
        obj.subZ = index;
      });
      console.log('[LAYER UP] New subZ values assigned:', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Zaktualizuj tablice w stanie
      const change = (arr, arrayName) =>
        arr.map((originalObjInArray) => { // Zmieniono nazwę zmiennej, aby uniknąć konfliktu
          const updatedObj = reorderedObjects.find(updated => updated.id === originalObjInArray.id && updated._arrayName === arrayName);
          // Zachowujemy wszystkie oryginalne właściwości i nadpisujemy tylko subZ
          return updatedObj ? { ...originalObjInArray, subZ: updatedObj.subZ } : originalObjInArray;
        });

      const newTexts = change(pg.texts, 'texts');
      const newImages = change(pg.images, 'images');
      const newSvgs = change(pg.svgs, 'svgs');
      
      console.log('[LAYER UP] Updated texts (actual state):', newTexts.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[LAYER UP] Updated images (actual state):', newImages.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[LAYER UP] Updated svgs (actual state):', newSvgs.map(o => ({ id: o.id, subZ: o.subZ })));

      pg.texts = newTexts;
      pg.images = newImages;
      pg.svgs = newSvgs;
      return pg;
    }); 
    incChangesAndMaybeAutoSave(); 
    console.log('[LAYER UP] Function completed');
  }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);

  const handleLayerDownLocal = useCallback((shapeId) => { 
    console.log('[LAYER DOWN] Called with shapeId:', shapeId);
    if (isAccepted && publicMode) return; 
    if (!shapeId) return; 
    if (foundObj && foundObj.id === shapeId) { 
      pushToHistory({ ...foundObj }); 
    } 
    updateCurrentPage((pg) => {
      // Zbieramy wszystkie obiekty z wszystkich tablic
      const allObjects = [
        ...pg.texts.map(obj => ({ ...obj, _type: 'text', _arrayName: 'texts' })),
        ...pg.images.map(obj => ({ ...obj, _type: 'image', _arrayName: 'images' })),
        ...pg.svgs.map(obj => ({ ...obj, _type: 'svg', _arrayName: 'svgs' }))
      ];
      console.log('[LAYER DOWN] All objects collected (before sort):', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Znajdź obiekt do przesunięcia
      const targetObj = allObjects.find(obj => obj.id === shapeId);
      console.log('[LAYER DOWN] Target object:', targetObj ? {id: targetObj.id, type: targetObj._type, subZ: targetObj.subZ, arrayName: targetObj._arrayName} : null);
      if (!targetObj) {
        console.log('[LAYER DOWN] Target object not found, returning unchanged page');
        return pg;
      }

      // Sortuj wszystkie obiekty według subZ
      allObjects.sort((a, b) => (a.subZ || 0) - (b.subZ || 0));
      console.log('[LAYER DOWN] Objects sorted by subZ:', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Znajdź indeks obiektu w posortowanej tablicy
      const currentIndex = allObjects.findIndex(obj => obj.id === shapeId);
      console.log('[LAYER DOWN] Current index:', currentIndex, 'Total objects:', allObjects.length);

      // Jeśli obiekt jest już na dole, nie rób nic
      if (currentIndex <= 0) {
        console.log('[LAYER DOWN] Object already at the bottom');
        return pg;
      }

      // Przenieś obiekt o jedną pozycję niżej
      const reorderedObjects = [...allObjects];
      const [movedObject] = reorderedObjects.splice(currentIndex, 1);
      reorderedObjects.splice(currentIndex - 1, 0, movedObject);
      console.log('[LAYER DOWN] Objects reordered (before new subZ):', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Przypisz nowe wartości subZ
      reorderedObjects.forEach((obj, index) => {
        obj.subZ = index;
      });
      console.log('[LAYER DOWN] New subZ values assigned:', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Zaktualizuj tablice w stanie
      const change = (arr, arrayName) =>
        arr.map((originalObjInArray) => {
          const updatedObj = reorderedObjects.find(updated => updated.id === originalObjInArray.id && updated._arrayName === arrayName);
          return updatedObj ? { ...originalObjInArray, subZ: updatedObj.subZ } : originalObjInArray;
        });

      const newTexts = change(pg.texts, 'texts');
      const newImages = change(pg.images, 'images');
      const newSvgs = change(pg.svgs, 'svgs');

      console.log('[LAYER DOWN] Updated texts (actual state):', newTexts.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[LAYER DOWN] Updated images (actual state):', newImages.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[LAYER DOWN] Updated svgs (actual state):', newSvgs.map(o => ({ id: o.id, subZ: o.subZ })));
      
      pg.texts = newTexts;
      pg.images = newImages;
      pg.svgs = newSvgs;
      return pg;
    }); 
    incChangesAndMaybeAutoSave(); 
  }, [isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave]);

  const handleMoveToTopLocal = useCallback(() => { 
    console.log('[MOVE TO TOP] Called with selectedShapeId:', selectedShapeId);
    if (!selectedShapeId) return; 
    if (isAccepted && publicMode) return; 
    if (foundObj && foundObj.id === selectedShapeId) { 
      pushToHistory({ ...foundObj }); 
    } 
    updateCurrentPage((pg) => {
      // Zbieramy wszystkie obiekty z wszystkich tablic
      const allObjects = [
        ...pg.texts.map(obj => ({ ...obj, _type: 'text', _arrayName: 'texts' })),
        ...pg.images.map(obj => ({ ...obj, _type: 'image', _arrayName: 'images' })),
        ...pg.svgs.map(obj => ({ ...obj, _type: 'svg', _arrayName: 'svgs' }))
      ];
      console.log('[MOVE TO TOP] All objects collected (before sort):', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Znajdź obiekt do przesunięcia
      const targetObj = allObjects.find(obj => obj.id === selectedShapeId);
      console.log('[MOVE TO TOP] Target object:', targetObj ? {id: targetObj.id, type: targetObj._type, subZ: targetObj.subZ, arrayName: targetObj._arrayName} : null);
      if (!targetObj) {
        console.log('[MOVE TO TOP] Target object not found, returning unchanged page');
        return pg;
      }

      // Sortuj wszystkie obiekty według subZ (potrzebne do prawidłowego określenia "góry")
      allObjects.sort((a, b) => (a.subZ || 0) - (b.subZ || 0));
      console.log('[MOVE TO TOP] Objects sorted by subZ (for reference):', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Usuń obiekt z obecnej pozycji i dodaj na koniec (na górę)
      const filteredObjects = allObjects.filter(obj => obj.id !== selectedShapeId);
      const reorderedObjects = [...filteredObjects, targetObj]; // targetObj jest dodawany na koniec
      console.log('[MOVE TO TOP] Objects reordered (before new subZ):', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Przypisz nowe wartości subZ
      reorderedObjects.forEach((obj, index) => {
        obj.subZ = index;
      });
      console.log('[MOVE TO TOP] New subZ values assigned:', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Zaktualizuj tablice w stanie
      const change = (arr, arrayName) =>
        arr.map((originalObjInArray) => {
          const updatedObj = reorderedObjects.find(updated => updated.id === originalObjInArray.id && updated._arrayName === arrayName);
          return updatedObj ? { ...originalObjInArray, subZ: updatedObj.subZ } : originalObjInArray;
        });

      const newTexts = change(pg.texts, 'texts');
      const newImages = change(pg.images, 'images');
      const newSvgs = change(pg.svgs, 'svgs');

      console.log('[MOVE TO TOP] Updated texts (actual state):', newTexts.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[MOVE TO TOP] Updated images (actual state):', newImages.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[MOVE TO TOP] Updated svgs (actual state):', newSvgs.map(o => ({ id: o.id, subZ: o.subZ })));
      
      pg.texts = newTexts;
      pg.images = newImages;
      pg.svgs = newSvgs;
      return pg;
    }); 
    incChangesAndMaybeAutoSave(); 
  }, [ selectedShapeId, isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave ]);

  const handleMoveToBottomLocal = useCallback(() => { 
    console.log('[MOVE TO BOTTOM] Called with selectedShapeId:', selectedShapeId);
    if (!selectedShapeId) return; 
    if (isAccepted && publicMode) return; 
    if (foundObj && foundObj.id === selectedShapeId) { 
      pushToHistory({ ...foundObj }); 
    } 
    updateCurrentPage((pg) => {
      // Zbieramy wszystkie obiekty z wszystkich tablic
      const allObjects = [
        ...pg.texts.map(obj => ({ ...obj, _type: 'text', _arrayName: 'texts' })),
        ...pg.images.map(obj => ({ ...obj, _type: 'image', _arrayName: 'images' })),
        ...pg.svgs.map(obj => ({ ...obj, _type: 'svg', _arrayName: 'svgs' }))
      ];
      console.log('[MOVE TO BOTTOM] All objects collected (before sort):', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Znajdź obiekt do przesunięcia
      const targetObj = allObjects.find(obj => obj.id === selectedShapeId);
      console.log('[MOVE TO BOTTOM] Target object:', targetObj ? {id: targetObj.id, type: targetObj._type, subZ: targetObj.subZ, arrayName: targetObj._arrayName} : null);
      if (!targetObj) {
        console.log('[MOVE TO BOTTOM] Target object not found, returning unchanged page');
        return pg;
      }

      // Sortuj wszystkie obiekty według subZ (potrzebne do prawidłowego określenia "dołu")
      allObjects.sort((a, b) => (a.subZ || 0) - (b.subZ || 0));
      console.log('[MOVE TO BOTTOM] Objects sorted by subZ (for reference):', allObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));
      
      // Usuń obiekt z obecnej pozycji i dodaj na początek (na dół)
      const filteredObjects = allObjects.filter(obj => obj.id !== selectedShapeId);
      const reorderedObjects = [targetObj, ...filteredObjects]; // targetObj jest dodawany na początek
      console.log('[MOVE TO BOTTOM] Objects reordered (before new subZ):', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Przypisz nowe wartości subZ
      reorderedObjects.forEach((obj, index) => {
        obj.subZ = index;
      });
      console.log('[MOVE TO BOTTOM] New subZ values assigned:', reorderedObjects.map(o => ({id: o.id, type: o._type, subZ: o.subZ, arrayName: o._arrayName})));

      // Zaktualizuj tablice w stanie
      const change = (arr, arrayName) =>
        arr.map((originalObjInArray) => {
          const updatedObj = reorderedObjects.find(updated => updated.id === originalObjInArray.id && updated._arrayName === arrayName);
          return updatedObj ? { ...originalObjInArray, subZ: updatedObj.subZ } : originalObjInArray;
        });

      const newTexts = change(pg.texts, 'texts');
      const newImages = change(pg.images, 'images');
      const newSvgs = change(pg.svgs, 'svgs');

      console.log('[MOVE TO BOTTOM] Updated texts (actual state):', newTexts.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[MOVE TO BOTTOM] Updated images (actual state):', newImages.map(o => ({ id: o.id, subZ: o.subZ })));
      console.log('[MOVE TO BOTTOM] Updated svgs (actual state):', newSvgs.map(o => ({ id: o.id, subZ: o.subZ })));
      
      pg.texts = newTexts;
      pg.images = newImages;
      pg.svgs = newSvgs;
      return pg;
    }); 
    incChangesAndMaybeAutoSave(); 
  }, [ selectedShapeId, isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave ]);

  const handleAddNewTextLocal = useCallback(() => { if (isAccepted && publicMode) return; updateCurrentPage((pg) => { let maxSubZ = 0; [...pg.texts, ...pg.images, ...pg.svgs].forEach((obj) => { if ((obj.subZ || 0) > maxSubZ) { maxSubZ = obj.subZ || 0; } }); const newId = "text-" + Date.now(); pg.texts = [ ...pg.texts, { id: newId, text: "Nowy tekst", x: 50, y: 50, draggable: true, color: [0, 0, 0], alignment: "left", fontName: "sans-serif", fontSize: 24, rotation: 0, scaleX: 1, scaleY: 1, zGroup: 3, subZ: maxSubZ + 1, flags: { noPrint: false, gold: false, locked: false, personalize: false } } ]; return pg; }); incChangesAndMaybeAutoSave(); }, [isAccepted, publicMode, updateCurrentPage, incChangesAndMaybeAutoSave]);

  // Zmodyfikowano handleImageDroppedLocal, aby renderowała SVG do offscreen canvas
  // i używała alternatywnego podejścia do skalowania
  const handleImageDroppedLocal = useCallback(async (args) => {
    console.log("[handleImageDroppedLocal] Entered function with args:", args);
    const { file, name, base64, customSvgUrl, customPos, customSize, isRebus, isBlissaroPlaceholder, placeholderUrl } = args;

    if (isAccepted && publicMode) {
        console.log("[handleImageDroppedLocal] Exiting: Project accepted in public mode.");
        return;
    }

    const finalFile = isRebus ? file : file;
    const finalName = isRebus ? file?.name : name;
    const initialX = isRebus ? customPos?.x : 50;
    const initialY = isRebus ? customPos?.y : 50;
    const initialW = isRebus ? customSize?.w : 200; // Oryginalne/odczytane wymiary
    const initialH = isRebus ? customSize?.h : 200;

    // === NOWA LOGIKA: Obsługa placeholder QR ===
    if (isBlissaroPlaceholder) {
      console.log("[handleImageDroppedLocal] Adding Blissaro placeholder QR...");
      try {
        // Nie robimy uploadu, używamy prawdziwego kodu QR z API
        const placeholderWidth = 100; // Stały rozmiar placeholder'a
        const placeholderHeight = 100;
        
        // Pobierz prawdziwy kod QR z linkiem do Blissaro
        const baseUrl = backendUrl.replace(/\/+$/, "");
        const qrResponse = await fetch(`${baseUrl}/public/generate-placeholder-qr`);
        
        if (!qrResponse.ok) {
          throw new Error(`HTTP ${qrResponse.status}: ${qrResponse.statusText}`);
        }
        
        const qrData = await qrResponse.json();
        
        if (!qrData.success || !qrData.qrCodeDataURL) {
          throw new Error(qrData.error || 'Nie udało się wygenerować kodu QR');
        }
        
        const qrCodeDataURL = qrData.qrCodeDataURL; // To jest już base64 data URL

        console.log("[handleImageDroppedLocal] Generated real QR code for URL:", qrData.url);
        
        updateCurrentPage((pg) => {
          let maxSubZ = 0;
          [...pg.texts, ...pg.images, ...pg.svgs].forEach((obj) => {
            if ((obj.subZ || 0) > maxSubZ) {
              maxSubZ = obj.subZ || 0;
            }
          });
          
          const newId = "img-blissaro-placeholder-" + Date.now();
          const placeholderObj = {
            id: newId,
            x: 50, // Pozycja początkowa
            y: 50,
            draggable: true,
            width: placeholderWidth,
            height: placeholderHeight,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            src: qrCodeDataURL, // Prawdziwy kod QR jako base64 data URL
            subZ: maxSubZ + 1,
            flags: { 
              noPrint: false, 
              gold: false, 
              locked: false, 
              personalize: false,
              blissaroPlaceholder: true // Specjalna flaga
            },
            zGroup: 1
          };
          
          console.log("[handleImageDroppedLocal] Adding Blissaro placeholder QR object:", placeholderObj);
          pg.images = [...pg.images, placeholderObj];
          return pg;
        });
        
        incChangesAndMaybeAutoSave();
        return; // Kończymy tutaj dla placeholder'a
      } catch (ex) {
        console.error("[handleImageDroppedLocal] Placeholder QR Error:", ex);
        console.error("Nie udało się dodać placeholder QR: " + ex.message);
        return;
      }
    }
    // === KONIEC obsługi placeholder QR ===

    if (!finalFile || typeof finalFile !== "object" || !finalFile.size) {
        console.log("[handleImageDroppedLocal] Invalid file detected:", finalFile);
        alert("Nie wykryto poprawnego pliku...");
        return;
    }

    console.log(`[handleImageDroppedLocal] Uploading file (isRebus: ${!!isRebus}):`, finalName);
    try {
        const baseUrl = backendUrl.replace(/\/+$/, "");
        const url = publicMode ? `${baseUrl}/client/uploads` : `${baseUrl}/admin/uploads`;
        const formData = new FormData();
        formData.append("file", finalFile);
        const uploadRes = await fetch(url, { method: "POST", credentials: "include", body: formData });
        if (!uploadRes.ok) {
            throw new Error(`Błąd uploadu (status=${uploadRes.status})`);
        }
        const uploadData = await uploadRes.json();
        if (!uploadData.success || !uploadData.filePath) {
            throw new Error("Serwer nie zwrócił ścieżki do pliku.");
        }
        const filePath = uploadData.filePath;
        const rawUrlPrefix = publicMode ? `${baseUrl}/client/raw-file?path=` : `${baseUrl}/admin/raw-file?path=`;
        const rawUrl = rawUrlPrefix + encodeURIComponent(filePath);
        console.log(`[handleImageDroppedLocal] Upload successful. File path: ${filePath}, Raw URL: ${rawUrl}`);

        const isSvg = (finalName || "").toLowerCase().endsWith(".svg");
        let konvaWidth = initialW;     // Wymiary obiektu Konva
        let konvaHeight = initialH;
        let konvaScaleX = 1;           // Skala obiektu Konva
        let konvaScaleY = 1;
        let konvaImageSource = rawUrl; // Domyślnie URL

        if (isSvg) {
            // Dla SVG, używamy oryginalnych wymiarów przekazanych z drag&drop
            // Nie potrzebujemy już ładować obrazka, aby je poznać
            konvaWidth = initialW;
            konvaHeight = initialH;
            konvaScaleX = 1;
            konvaScaleY = 1;
            konvaImageSource = rawUrl; // Zapisujemy URL jako źródło
            console.log(`[handleImageDroppedLocal] Processing SVG. Using dimensions: ${konvaWidth}x${konvaHeight}, Source URL: ${rawUrl}`);
        } else {
            // Obsługa obrazów rastrowych (logika bez zmian - próbujemy załadować, aby uzyskać wymiary)
            try {
                const img = new window.Image();
                img.crossOrigin = "anonymous";
                await new Promise((resolve, reject) => {
                    img.onload = () => resolve(true);
                    img.onerror = reject;
                    img.src = rawUrl;
                });
                const origW = img.naturalWidth || 100;
                const origH = img.naturalHeight || 100;
                // Obliczamy skalę, aby zmieścić obraz na stronie, ale nie większy niż 1:1
                const currentPageData = pages[currentPage] || { baseWidth: 800, baseHeight: 600 }; // Domyślne wymiary strony
                const scale = Math.min((currentPageData.baseWidth || 800) / origW, (currentPageData.baseHeight || 600) / origH, 1);
                konvaWidth = Math.round(origW * scale); // Ustawiamy wymiary Konva
                konvaHeight = Math.round(origH * scale);
                console.log(`[handleImageDroppedLocal] Raster image loaded. Original: ${origW}x${origH}, Scaled: ${konvaWidth}x${konvaHeight}`);
            } catch (imgLoadError) {
                console.warn("[handleImageDroppedLocal] Could not load raster image to determine size, using default:", imgLoadError);
                konvaWidth = initialW; // Używamy domyślnych/przekazanych wymiarów
                konvaHeight = initialH;
            }
            konvaImageSource = rawUrl; // Źródłem jest URL
            konvaScaleX = 1; // Skala jest już uwzględniona w konvaWidth/konvaHeight
            konvaScaleY = 1;
        }

        // Dodajemy obiekt do stanu strony
        updateCurrentPage((pg) => {
            let maxSubZ = 0;
            [...pg.texts, ...pg.images, ...pg.svgs].forEach((obj) => {
                if ((obj.subZ || 0) > maxSubZ) {
                    maxSubZ = obj.subZ || 0;
                }
            });
            const newId = (isSvg ? "svg-" : "img-") + Date.now();
            const commonProps = {
                id: newId,
                x: initialX,
                y: initialY,
                draggable: true,
                width: konvaWidth,      // Używamy obliczonych/oryginalnych wymiarów
                height: konvaHeight,     // Używamy obliczonych/oryginalnych wymiarów
                rotation: 0,
                scaleX: konvaScaleX,    // Zwykle 1, chyba że skalowanie jest potrzebne inaczej
                scaleY: konvaScaleY,    // Zwykle 1
                filePath,               // Zapisujemy ścieżkę do pliku
                src: konvaImageSource,  // Zapisujemy URL jako źródło
                subZ: maxSubZ + 1,
                flags: { noPrint: false, gold: false, locked: false, personalize: false }
            };

            if (isSvg) {
                const svgObj = {
                    ...commonProps,
                    // Usunięto 'image' - używamy 'src' z commonProps
                    originalWidth: konvaWidth, // Zapisujemy wymiary jako oryginalne
                    originalHeight: konvaHeight,
                    zGroup: 2,
                    color: [0, 0, 0] // Domyślny kolor dla SVG
                };
                console.log("[handleImageDroppedLocal] Adding SVG object to state (using URL source):", svgObj);
                pg.svgs = [...pg.svgs, svgObj];
            } else {
                const imgObj = {
                    ...commonProps,
                    // 'src' jest już w commonProps
                    zGroup: 1
                };
                console.log("[handleImageDroppedLocal] Adding Image object to state (using URL source):", imgObj);
                pg.images = [...pg.images, imgObj];
            }
            return pg;
        });
        console.log("[handleImageDroppedLocal] State update requested with new object.");
        incChangesAndMaybeAutoSave();
    } catch (ex) {
        console.error("[handleImageDroppedLocal] Upload Error:", ex);
        alert("Nie udało się wgrać pliku: " + ex.message);
    }
}, [isAccepted, publicMode, updateCurrentPage, incChangesAndMaybeAutoSave, backendUrl]);


  const handleToggleFlagLocal = useCallback((flagName) => { if (!selectedShapeId) return; if (isAccepted && publicMode) return; if (foundObj && foundObj.id === selectedShapeId) { pushToHistory({ ...foundObj }); } updateCurrentPage((pg) => { function toggleIn(arr) { return arr.map((obj) => { if (obj.id === selectedShapeId) { const currentFlags = obj.flags || { noPrint: false, gold: false, locked: false, personalize: false }; return { ...obj, flags: { ...currentFlags, [flagName]: !currentFlags[flagName] } }; } return obj; }); } pg.texts = toggleIn(pg.texts); pg.images = toggleIn(pg.images); pg.svgs = toggleIn(pg.svgs); return pg; }); incChangesAndMaybeAutoSave(); }, [ selectedShapeId, isAccepted, publicMode, foundObj, pushToHistory, updateCurrentPage, incChangesAndMaybeAutoSave ]);

  // Obsługa Delete (bez zmian)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Delete") {
        if (!selectedShapeId) return;
        if (isAccepted && publicMode) {
          // W trybie publicznym i zaakceptowanym nie pozwalamy na usuwanie
          // Można dodać powiadomienie dla użytkownika, jeśli jest taka potrzeba
          return;
        }
        if (foundObj) {
          pushToHistory({ ...foundObj }); // Zapisz stan przed usunięciem
        }
        updateCurrentPage((pg) => ({
          ...pg,
          texts: pg.texts.filter((t) => t.id !== selectedShapeId),
          images: pg.images.filter((im) => im.id !== selectedShapeId),
          svgs: pg.svgs.filter((sv) => sv.id !== selectedShapeId), // Dodano usuwanie SVG
        }));
        setSelectedShapeId(null); // Odznacz usunięty obiekt
        incChangesAndMaybeAutoSave(); // Zapisz zmiany
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedShapeId, foundObj, isAccepted, publicMode, pushToHistory, updateCurrentPage, setSelectedShapeId, incChangesAndMaybeAutoSave]);

  // Zwracamy wszystkie lokalne callbacki
  return {
    foundObj,
    handleChangeTextLocal,
    handleChangeImageLocal,
    handleChangeSvgLocal,
    handleColorChangeLocal,
    handleLayerUpLocal,
    handleLayerDownLocal,
    handleMoveToTopLocal,
    handleMoveToBottomLocal,
    handleAddNewTextLocal,
    handleImageDroppedLocal,
    handleToggleFlagLocal,
    // Nie ma potrzeby zwracać doAutoSaveLocal i incChangesAndMaybeAutoSave,
    // chyba że są używane bezpośrednio przez komponent Editor.js
  };
}
